import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Plus, TrendingUp, Loader2, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { launchPayment } from "@/lib/payments";
import { OrderTimeline } from "@/components/OrderTimeline";

const fmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

const STATUS = {
  awaiting_payment: "border-amber-400 text-amber-700",
  payment_reported: "border-sky-500/50 text-sky-700",
  pending_confirmation: "border-[#6D28D9]/50 text-[#6D28D9]",
  confirmed: "border-emerald-600/40 text-emerald-700",
  allotted: "border-emerald-700 bg-emerald-600 text-white",
  paid: "border-emerald-600/40 text-emerald-700",
};

export const Portfolio = () => {
  const [data, setData] = useState(null);
  const [topup, setTopup] = useState("");
  const [busy, setBusy] = useState(false);
  const [kyc, setKyc] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/kyc").then((r) => setKyc(r.data.status)).catch(() => {}); }, []);

  useEffect(() => { api.get("/portfolio").then((r) => setData(r.data)).catch(() => setData({ wallet_balance: 0, invested_total: 0, holdings: [] })); }, []);

  const addMoney = async () => {
    const amt = Number(topup);
    if (!(amt >= 100)) { toast.error("Minimum top-up is ₹100."); return; }
    setBusy(true);
    try {
      const { data: d } = await api.post("/wallet/topup", { amount: amt, origin_url: window.location.origin });
      await launchPayment(d, { description: "Wallet top-up", onCancel: () => setBusy(false) });
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  if (!data) return <p className="mt-6 font-mono2 text-xs uppercase tracking-[0.25em] text-slate-500">Loading portfolio…</p>;

  return (
    <div data-testid="portfolio">
      {kyc && kyc !== "submitted" && kyc !== "verified" && data.holdings.length > 0 && (
        <div data-testid="portfolio-kyc-nudge" className="mt-6 flex flex-wrap items-center justify-between gap-4 border border-[#6D28D9]/40 bg-[#6D28D9]/[0.06] p-5">
          <p className="flex items-center gap-2 text-sm text-slate-800"><ShieldCheck className="h-4 w-4 text-[#6D28D9]" /> Complete your KYC so the desk can transfer your shares to your demat account.</p>
          <Link data-testid="portfolio-kyc-link" to="/client/kyc" className="bg-[#6D28D9] px-4 py-2 font-mono2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#FFFFFF] hover:bg-[#5B21B6]">Complete KYC</Link>
        </div>
      )}
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="border border-slate-200 bg-[#F6F4FD]/70 p-6">
          <div className="flex items-center gap-2 text-[#6D28D9]"><Wallet className="h-4 w-4" /><p className="font-mono2 text-[10px] uppercase tracking-[0.2em]">Wallet balance</p></div>
          <p data-testid="wallet-balance" className="font-display mt-3 text-3xl font-semibold text-slate-900">₹{fmt.format(data.wallet_balance)}</p>
          <div className="mt-4 flex items-center gap-2">
            <input data-testid="wallet-topup-input" type="number" min={100} value={topup} onChange={(e) => setTopup(e.target.value)} placeholder="Amount ₹" className="w-32 border border-slate-200 bg-[#FFFFFF]/60 px-3 py-2.5 font-mono2 text-sm text-slate-800 outline-none focus:border-[#6D28D9]/60" />
            <button data-testid="wallet-topup-button" onClick={addMoney} disabled={busy} className="flex items-center gap-1.5 bg-[#6D28D9] px-4 py-2.5 font-mono2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6] disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3 w-3" /> Add money</>}
            </button>
          </div>
        </div>
        <div className="border border-slate-200 bg-[#F6F4FD]/70 p-6">
          <div className="flex items-center gap-2 text-[#6D28D9]"><TrendingUp className="h-4 w-4" /><p className="font-mono2 text-[10px] uppercase tracking-[0.2em]">Invested</p></div>
          <p data-testid="invested-total" className="font-display mt-3 text-3xl font-semibold text-slate-900">₹{fmt.format(data.invested_total)}</p>
          <p className="mt-4 font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500">{data.holdings.length} holding{data.holdings.length === 1 ? "" : "s"}</p>
        </div>
      </div>

      <div data-testid="holdings-list" className="mt-6 space-y-px border border-slate-200 bg-slate-200">
        {data.holdings.map((h) => (
          <div key={h.id} data-testid={`holding-${h.slug}`} className="bg-[#F6F4FD] px-5 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-display text-lg font-medium text-slate-900">{h.company_name}</p>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500">{h.quantity} share(s) · ₹{fmt.format(h.unit_price)} · {new Date(h.created_at).toLocaleDateString("en-IN")}{h.reference ? ` · ${h.reference}` : ""}</p>
              </div>
              <div className="flex items-center gap-4">
                <p className="font-display text-lg font-semibold text-[#6D28D9]">₹{fmt.format(h.amount)}</p>
                <span className={`border px-2.5 py-1 font-mono2 text-[9px] uppercase tracking-[0.15em] ${STATUS[h.status] || STATUS.paid}`}>{(h.status || "paid").replace(/_/g, " ")}</span>
                <button data-testid={`holding-timeline-toggle-${h.id}`} onClick={() => setOpen(open === h.id ? null : h.id)} className="flex items-center gap-1 font-mono2 text-[9px] uppercase tracking-[0.15em] text-slate-500 hover:text-[#6D28D9]">Track {open === h.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}</button>
              </div>
            </div>
            {open === h.id && h.timeline && <div className="mt-4 border-t border-slate-200 pt-4"><OrderTimeline timeline={h.timeline} testid={`holding-timeline-${h.id}`} /></div>}
          </div>
        ))}
        {data.holdings.length === 0 && (
          <p data-testid="no-holdings" className="bg-[#F6F4FD] px-5 py-12 text-center font-mono2 text-xs uppercase tracking-[0.25em] text-slate-700">No holdings yet — buy a share from the board</p>
        )}
      </div>
    </div>
  );
};
